import React from 'react'
import { getLeftPercent, getWidthPercent, APPOINTMENT_COLORS } from './timelineConstants'
import { SegmentedBlock, Segment } from './SegmentedBlock'
import { ResizeHandle } from '../../../features/disposition'

export type { ResizeHandle }

interface Props {
  anfahrtStartIso: string
  travelDuration: number
  workDuration: number
  departureDuration: number
  committed?: boolean
  color?: string
  isColliding?: boolean
  onResizeStart?: (handle: ResizeHandle, e: React.MouseEvent) => void
  onBlockDragStart?: (e: React.MouseEvent) => void
}

const ResizeHandleDiv = ({
  style,
  onMouseDown,
}: {
  style: React.CSSProperties
  onMouseDown: (e: React.MouseEvent) => void
}) => (
  <div
    className="absolute z-10 cursor-ew-resize hover:bg-white/40 transition-colors"
    style={{ top: 0, bottom: 0, width: 6, ...style }}
    onMouseDown={(e) => {
      e.stopPropagation()
      onMouseDown(e)
    }}
  />
)

const COLLISION_COLOR = '#ef4444'

export const GhostAppointmentBlock = ({
  anfahrtStartIso,
  travelDuration,
  workDuration,
  departureDuration,
  committed,
  color,
  isColliding,
  onResizeStart,
  onBlockDragStart,
}: Props) => {
  const totalDuration = travelDuration + workDuration + departureDuration
  const left = getLeftPercent(anfahrtStartIso)
  const width = getWidthPercent(totalDuration)

  const handleSegmentMouseDown = (e: React.MouseEvent) => {
    if (!committed) return
    e.stopPropagation()
    onBlockDragStart?.(e)
  }

  const travelColor = isColliding ? COLLISION_COLOR : (color ?? APPOINTMENT_COLORS.travel)
  const workColor = isColliding ? COLLISION_COLOR : (color ?? APPOINTMENT_COLORS.work)

  const segments: Segment[] = [
    { duration: travelDuration, color: travelColor, cursor: committed ? 'grab' : undefined, onMouseDown: handleSegmentMouseDown },
    { duration: workDuration, color: workColor, cursor: committed ? 'grab' : undefined, onMouseDown: handleSegmentMouseDown },
    { duration: departureDuration, color: travelColor, cursor: committed ? 'grab' : undefined, onMouseDown: handleSegmentMouseDown },
  ]

  const travelFraction = totalDuration > 0 ? travelDuration / totalDuration : 0
  const workFraction = totalDuration > 0 ? (travelDuration + workDuration) / totalDuration : 0

  return (
    <SegmentedBlock
      segments={segments}
      left={left}
      width={width}
      className="opacity-70 ring-2 ring-white/70 ring-inset"
      style={{ pointerEvents: committed ? 'auto' : 'none' }}
    >
      {committed && onResizeStart && (
        <>
          <ResizeHandleDiv
            style={{ left: 0 }}
            onMouseDown={(e) => onResizeStart('left', e)}
          />
          {travelDuration > 0 && (
            <ResizeHandleDiv
              style={{ left: `${travelFraction * 100}%`, marginLeft: -3 }}
              onMouseDown={(e) => onResizeStart('travel-work', e)}
            />
          )}
          <ResizeHandleDiv
            style={{ left: `${workFraction * 100}%`, marginLeft: -3 }}
            onMouseDown={(e) => onResizeStart('work-departure', e)}
          />
          <ResizeHandleDiv
            style={{ right: 0 }}
            onMouseDown={(e) => onResizeStart('right', e)}
          />
        </>
      )}
    </SegmentedBlock>
  )
}
